import { create } from 'zustand';

/**
 * Tracks report-generation jobs so the "Report queue" modal (mounted globally in
 * Layout) can show what is still being built/sent, independent of the
 * Dashboard-only GenerateReportModal whose local `generating` flag is lost on close.
 *
 * Job lifecycle: 'building' -> 'downloaded' | 'failed'. Finished jobs are kept
 * (not auto-cleared) so the user retains a history until they dismiss them.
 */

let seq = 0;
const genId = () => `rpt_${Date.now().toString(36)}_${(++seq).toString(36)}`;

export const useReportQueueStore = create((set, get) => ({
  jobs: [],

  /** Add a job in the 'building' state; returns its id. */
  addJob: ({ label, reportType, city, dateRange } = {}) => {
    const id = genId();
    const job = {
      id,
      label: label || 'Report',
      reportType: reportType || 'rabies',
      city: city || '',
      dateRange: dateRange || '',
      status: 'building', // 'building' | 'downloaded' | 'failed'
      error: null,
      createdAt: Date.now(),
    };
    set((s) => ({ jobs: [job, ...s.jobs] }));
    return id;
  },

  updateJob: (id, patch) =>
    set((s) => ({ jobs: s.jobs.map((j) => (j.id === id ? { ...j, ...patch } : j)) })),

  removeJob: (id) =>
    set((s) => ({ jobs: s.jobs.filter((j) => j.id !== id) })),

  /** Mark a job as downloaded; the entry is kept until the user dismisses it. */
  markDownloaded: (id) => get().updateJob(id, { status: 'downloaded' }),

  markFailed: (id, error) =>
    get().updateJob(id, { status: 'failed', error: error || 'Failed to prepare' }),

  /** Drop everything that is no longer in progress. */
  clearFinished: () =>
    set((s) => ({
      jobs: s.jobs.filter((j) => j.status === 'building'),
    })),
}));
